import { useState } from 'react';

import { Button } from '@/components/ui/Button';
import { ErrorDialog } from '@/components/ui/ErrorDialog';
import { useAuth } from '@/lib/auth';

import { adminBulkIndex, type AdminBulkIndexResult } from '../api';

const PLACEHOLDER = `# registry,name,version
npm,react,18.3.1
npm,next,16.2.4
pypi,fastapi,0.110.0
pub,provider,6.1.2`;

export function AdminBulkIndexPanel() {
  const { getToken } = useAuth();
  const [csv, setCsv] = useState('');
  const [autoApprove, setAutoApprove] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<AdminBulkIndexResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!csv.trim()) {
      setError('Paste at least one row before submitting.');
      return;
    }
    setSubmitting(true);
    setResult(null);
    try {
      const token = await getToken();
      if (!token) throw new Error('You must be signed in as an admin.');
      const r = await adminBulkIndex({ csv, auto_approve: autoApprove }, token);
      setResult(r);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bulk import failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="sheet p-4">
      <header className="mb-3">
        <h3 className="text-base font-semibold text-ink">Bulk index (admin)</h3>
        <p className="mt-1 text-sm text-dim">
          Paste a CSV of <code>registry,name,version</code> rows. Each row is checked against the
          public registry; valid rows become index requests. Comment lines start with <code>#</code>
          .
        </p>
      </header>

      <textarea
        value={csv}
        onChange={(e) => setCsv(e.target.value)}
        placeholder={PLACEHOLDER}
        rows={10}
        spellCheck={false}
        className="w-full border border-rule bg-code-bg p-2 font-mono text-xs text-ink"
      />

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-link">
          <input
            type="checkbox"
            checked={autoApprove}
            onChange={(e) => setAutoApprove(e.target.checked)}
          />
          Auto-approve (skip pending_approval, queue immediately)
        </label>
        <Button onClick={submit} disabled={submitting || !csv.trim()}>
          {submitting ? 'Submitting…' : 'Submit'}
        </Button>
      </div>

      {result ? (
        <div className="mt-4 space-y-2 text-sm">
          <p className="text-link">
            Submitted <strong>{result.counts.submitted}</strong>, skipped{' '}
            <strong>{result.counts.skipped}</strong>
            {result.auto_approve ? ' — auto-approved.' : ' — awaiting approval.'}
          </p>
          {result.submitted.length > 0 ? (
            <details className="rounded border border-rule p-2">
              <summary className="cursor-pointer text-link">
                Submitted ({result.submitted.length})
              </summary>
              <ul className="mt-2 space-y-1 font-mono text-xs">
                {result.submitted.map((s) => (
                  <li key={s.request_id} className="text-link">
                    {s.registry} / {s.name} @ {s.version} — {s.status}
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
          {result.skipped.length > 0 ? (
            <details className="rounded border border-warn/30 p-2" open>
              <summary className="cursor-pointer text-warn">
                Skipped ({result.skipped.length})
              </summary>
              <ul className="mt-2 space-y-1 font-mono text-xs">
                {result.skipped.map((s, i) => (
                  <li key={i} className="text-warn">
                    {s.registry ? `${s.registry} / ${s.name} @ ${s.version} — ` : ''}
                    {s.reason}
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
        </div>
      ) : null}

      {error !== null ? <ErrorDialog message={error} onClose={() => setError(null)} /> : null}
    </section>
  );
}
